import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Botões: borda 6px, sem gradiente — docs UX OilGest.
 *
 * Linguagem de cor (docs-arquitetura/13 §5.3, decisão "Opção A"):
 * - `default` (LARANJA da marca) = a ação que EXECUTA — Salvar, Importar,
 *   Nova OS, Finalizar, Abrir/Fechar caixa. Máx. 1–2 por tela.
 * - `blue` (azul primário sólido) = estado ativo de abas/filtros/seleção —
 *   navegação, nunca submissão.
 * - `outline-primary` (contorno azul) = ação de apoio com destaque leve
 *   (Baixar modelo, exportar).
 * - `success` (verde) = dinheiro entrando (Pagar OS, receber).
 * - `destructive` (vermelho) = destrutivo/cancelamento.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 min-h-10 px-4 py-2 [&_svg]:shrink-0 active:scale-[0.98] active:shadow-none',
  {
    variants: {
      variant: {
        default: 'bg-brand font-semibold text-brand-foreground hover:bg-brand/90 shadow-sm hover:shadow',
        blue: 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm hover:shadow',
        'outline-primary': 'border border-primary bg-card text-primary hover:bg-primary/10 shadow-sm',
        secondary: 'border border-border bg-muted text-foreground hover:bg-muted/70 shadow-sm',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm hover:shadow',
        success: 'bg-success text-success-foreground hover:bg-success/90',
        outline: 'border border-border bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground shadow-sm',
        ghost: 'text-muted-foreground hover:bg-accent hover:text-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        // Alvo de toque de 44px no mobile (Apple/Google); sm: volta ao tamanho
        // original a partir do breakpoint sm/640px — desktop fica igual.
        default: 'h-11 sm:h-10 px-4 py-2',
        sm: 'h-10 sm:h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-11 w-11 sm:h-10 sm:w-10 min-h-10 min-w-10 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
